/**
 * quoteExport.js
 * Generates a professional WTR quote Word document using the `docx` npm package.
 * Called from QuoteDetailPage.js via: exportQuoteToDocx(quoteData, lineItems, client)
 *
 * Install: npm install docx   (already included in package.json for Session 2)
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  ShadingType,
  VerticalAlign,
  HeadingLevel,
} from 'docx'

// ─── Style constants ─────────────────────────────────────────────────────────

const NAVY   = '0F1F3D'
const GREEN  = '2A9D5C'
const LIGHT_GREY = 'F5F6F8'
const MID_GREY   = 'E2E5EB'
const WHITE  = 'FFFFFF'
const FONT   = 'Calibri'

// DXA units: 1 inch = 1440 DXA, content width on letter with 1" margins = 9360
const COL_WIDTHS = [5200, 1200, 1480, 1480] // description | qty | unit price | total
const TABLE_WIDTH = COL_WIDTHS.reduce((a, b) => a + b, 0) // 9360

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtMoney(n) {
  return '$' + parseFloat(n || 0).toLocaleString('en-CA', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
}

function fmtDate(dateStr) {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-CA', {
      year: 'numeric', month: 'long', day: 'numeric',
    })
  } catch { return dateStr }
}

function noBorder() {
  const none = { style: BorderStyle.NONE, size: 0, color: WHITE }
  return { top: none, bottom: none, left: none, right: none }
}

function thinBorder() {
  const b = { style: BorderStyle.SINGLE, size: 4, color: MID_GREY }
  return { top: b, bottom: b, left: b, right: b }
}

function cell(content, opts = {}) {
  const {
    bold = false, mono = false, right = false, center = false,
    shading, borders, colSpan, verticalAlign, color,
    fontSize = 22, muted = false,
  } = opts

  const runs = typeof content === 'string'
    ? [new TextRun({
        text: content,
        bold,
        font: mono ? 'Courier New' : FONT,
        size: fontSize,
        color: muted ? '8A93A6' : (color || '0F1F3D'),
      })]
    : content // allow passing array of TextRun

  return new TableCell({
    columnSpan: colSpan,
    verticalAlign: verticalAlign || VerticalAlign.CENTER,
    shading: shading ? { fill: shading, type: ShadingType.CLEAR, color: 'auto' } : undefined,
    borders: borders || thinBorder(),
    margins: { top: 80, bottom: 80, left: 120, right: 120 },
    width: undefined,
    children: [
      new Paragraph({
        alignment: center
          ? AlignmentType.CENTER
          : right
          ? AlignmentType.RIGHT
          : AlignmentType.LEFT,
        children: runs,
      }),
    ],
  })
}

function spacer(height = 160) {
  return new Paragraph({
    spacing: { before: 0, after: height },
    children: [new TextRun({ text: '' })],
  })
}

function sectionHeading(text) {
  return new Paragraph({
    spacing: { before: 240, after: 80 },
    children: [
      new TextRun({
        text: text.toUpperCase(),
        font: FONT, size: 18, bold: true, color: '5A6478',
      }),
    ],
    border: {
      bottom: { style: BorderStyle.SINGLE, size: 4, color: MID_GREY, space: 4 },
    },
  })
}

// ─── Main Export Function ─────────────────────────────────────────────────────

export async function exportQuoteToDocx(quoteData, lineItems, client) {
  const {
    quoteNumber, eventDate, eventType, address,
    subtotal, discountAmount = 0, discountedSubtotal,
    gst, pst, grandTotal,
    depositRequired = 0, depositReceived = 0,
    terms, notes,
  } = quoteData

  const balanceDue = Math.max(0, grandTotal - depositReceived)
  const today = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })

  // ── HEADER: WTR Banner ──────────────────────────────────────────────────────

  const headerTable = new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: [5760, 3600],
    borders: { top: { style: BorderStyle.NONE, size: 0 }, bottom: { style: BorderStyle.NONE, size: 0 }, left: { style: BorderStyle.NONE, size: 0 }, right: { style: BorderStyle.NONE, size: 0 }, insideH: { style: BorderStyle.NONE, size: 0 }, insideV: { style: BorderStyle.NONE, size: 0 } },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            shading: { fill: NAVY, type: ShadingType.CLEAR, color: 'auto' },
            borders: noBorder(),
            margins: { top: 200, bottom: 200, left: 240, right: 120 },
            width: { size: 5760, type: WidthType.DXA },
            children: [
              new Paragraph({
                children: [new TextRun({ text: 'WINNIPEG TENT RENTAL', font: FONT, size: 36, bold: true, color: WHITE })],
              }),
              new Paragraph({
                spacing: { before: 60 },
                children: [new TextRun({ text: 'RENTAL QUOTE', font: FONT, size: 22, color: '9FCFB4' })],
              }),
            ],
          }),
          new TableCell({
            shading: { fill: NAVY, type: ShadingType.CLEAR, color: 'auto' },
            borders: noBorder(),
            margins: { top: 200, bottom: 200, left: 120, right: 240 },
            width: { size: 3600, type: WidthType.DXA },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                children: [new TextRun({ text: quoteNumber || 'DRAFT', font: FONT, size: 28, bold: true, color: WHITE })],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { before: 60 },
                children: [new TextRun({ text: `Date: ${today}`, font: FONT, size: 20, color: 'B0BDD0' })],
              }),
            ],
          }),
        ],
      }),
    ],
  })

  // ── CLIENT + EVENT DETAILS ──────────────────────────────────────────────────

  const detailsTable = new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: [4680, 4680],
    rows: [
      // Header row
      new TableRow({
        children: [
          new TableCell({
            columnSpan: 1,
            shading: { fill: LIGHT_GREY, type: ShadingType.CLEAR, color: 'auto' },
            borders: thinBorder(),
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ children: [new TextRun({ text: 'BILL TO', font: FONT, size: 18, bold: true, color: '5A6478' })] })],
          }),
          new TableCell({
            columnSpan: 1,
            shading: { fill: LIGHT_GREY, type: ShadingType.CLEAR, color: 'auto' },
            borders: thinBorder(),
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ children: [new TextRun({ text: 'EVENT DETAILS', font: FONT, size: 18, bold: true, color: '5A6478' })] })],
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            borders: thinBorder(),
            margins: { top: 120, bottom: 120, left: 120, right: 120 },
            children: [
              new Paragraph({ children: [new TextRun({ text: client?.name || '—', font: FONT, size: 24, bold: true, color: NAVY })] }),
              ...(client?.company ? [new Paragraph({ children: [new TextRun({ text: client.company, font: FONT, size: 20, color: '5A6478' })] })] : []),
              ...(client?.email ? [new Paragraph({ spacing: { before: 60 }, children: [new TextRun({ text: client.email, font: FONT, size: 20, color: '5A6478' })] })] : []),
              ...(client?.phone ? [new Paragraph({ children: [new TextRun({ text: client.phone, font: FONT, size: 20, color: '5A6478' })] })] : []),
            ],
          }),
          new TableCell({
            borders: thinBorder(),
            margins: { top: 120, bottom: 120, left: 120, right: 120 },
            children: [
              ...(eventDate ? [
                new Paragraph({ children: [new TextRun({ text: 'Event Date:  ', font: FONT, size: 20, bold: true, color: NAVY }), new TextRun({ text: fmtDate(eventDate), font: FONT, size: 20 })] }),
              ] : []),
              ...(eventType ? [
                new Paragraph({ children: [new TextRun({ text: 'Event Type:  ', font: FONT, size: 20, bold: true, color: NAVY }), new TextRun({ text: eventType.charAt(0).toUpperCase() + eventType.slice(1), font: FONT, size: 20 })] }),
              ] : []),
              ...(address ? [
                new Paragraph({ spacing: { before: 60 }, children: [new TextRun({ text: 'Delivery Address:', font: FONT, size: 20, bold: true, color: NAVY })] }),
                new Paragraph({ children: [new TextRun({ text: address, font: FONT, size: 20, color: '5A6478' })] }),
              ] : []),
            ],
          }),
        ],
      }),
    ],
  })

  // ── LINE ITEMS TABLE ────────────────────────────────────────────────────────

  const lineItemsHeaderRow = new TableRow({
    tableHeader: true,
    children: [
      new TableCell({
        shading: { fill: NAVY, type: ShadingType.CLEAR, color: 'auto' },
        borders: noBorder(),
        margins: { top: 100, bottom: 100, left: 120, right: 80 },
        width: { size: COL_WIDTHS[0], type: WidthType.DXA },
        children: [new Paragraph({ children: [new TextRun({ text: 'DESCRIPTION', font: FONT, size: 18, bold: true, color: WHITE })] })],
      }),
      new TableCell({
        shading: { fill: NAVY, type: ShadingType.CLEAR, color: 'auto' },
        borders: noBorder(),
        margins: { top: 100, bottom: 100, left: 80, right: 80 },
        width: { size: COL_WIDTHS[1], type: WidthType.DXA },
        children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: 'QTY', font: FONT, size: 18, bold: true, color: WHITE })] })],
      }),
      new TableCell({
        shading: { fill: NAVY, type: ShadingType.CLEAR, color: 'auto' },
        borders: noBorder(),
        margins: { top: 100, bottom: 100, left: 80, right: 80 },
        width: { size: COL_WIDTHS[2], type: WidthType.DXA },
        children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: 'UNIT PRICE', font: FONT, size: 18, bold: true, color: WHITE })] })],
      }),
      new TableCell({
        shading: { fill: NAVY, type: ShadingType.CLEAR, color: 'auto' },
        borders: noBorder(),
        margins: { top: 100, bottom: 100, left: 80, right: 120 },
        width: { size: COL_WIDTHS[3], type: WidthType.DXA },
        children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: 'TOTAL', font: FONT, size: 18, bold: true, color: WHITE })] })],
      }),
    ],
  })

  const itemRows = (lineItems || []).map((li, i) => {
    const lineTotal = parseFloat(li.quantity || 0) * parseFloat(li.unit_price || 0)
    const rowShading = i % 2 === 0 ? WHITE : LIGHT_GREY
    const rowBorder = { style: BorderStyle.SINGLE, size: 2, color: MID_GREY }
    const rowBorders = { top: rowBorder, bottom: rowBorder, left: rowBorder, right: rowBorder }

    return new TableRow({
      children: [
        new TableCell({
          shading: { fill: rowShading, type: ShadingType.CLEAR, color: 'auto' },
          borders: rowBorders,
          margins: { top: 80, bottom: 80, left: 120, right: 80 },
          width: { size: COL_WIDTHS[0], type: WidthType.DXA },
          children: [new Paragraph({ children: [new TextRun({ text: li.description || '', font: FONT, size: 20 })] })],
        }),
        new TableCell({
          shading: { fill: rowShading, type: ShadingType.CLEAR, color: 'auto' },
          borders: rowBorders,
          margins: { top: 80, bottom: 80, left: 80, right: 80 },
          width: { size: COL_WIDTHS[1], type: WidthType.DXA },
          children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: String(li.quantity || ''), font: 'Courier New', size: 20 })] })],
        }),
        new TableCell({
          shading: { fill: rowShading, type: ShadingType.CLEAR, color: 'auto' },
          borders: rowBorders,
          margins: { top: 80, bottom: 80, left: 80, right: 80 },
          width: { size: COL_WIDTHS[2], type: WidthType.DXA },
          children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: fmtMoney(li.unit_price), font: 'Courier New', size: 20 })] })],
        }),
        new TableCell({
          shading: { fill: rowShading, type: ShadingType.CLEAR, color: 'auto' },
          borders: rowBorders,
          margins: { top: 80, bottom: 80, left: 80, right: 120 },
          width: { size: COL_WIDTHS[3], type: WidthType.DXA },
          children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: fmtMoney(lineTotal), font: 'Courier New', size: 20, bold: true })] })],
        }),
      ],
    })
  })

  const lineItemsTable = new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: COL_WIDTHS,
    rows: [lineItemsHeaderRow, ...itemRows],
  })

  // ── TOTALS TABLE ────────────────────────────────────────────────────────────

  function totalRow(label, value, highlight = false) {
    const shading = highlight ? GREEN : WHITE
    const textColor = highlight ? WHITE : NAVY
    return new TableRow({
      children: [
        new TableCell({
          columnSpan: 3,
          shading: { fill: highlight ? GREEN : WHITE, type: ShadingType.CLEAR, color: 'auto' },
          borders: thinBorder(),
          margins: { top: 60, bottom: 60, left: 120, right: 80 },
          children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: label, font: FONT, size: highlight ? 22 : 20, bold: highlight, color: highlight ? WHITE : '5A6478' })] })],
        }),
        new TableCell({
          shading: { fill: shading, type: ShadingType.CLEAR, color: 'auto' },
          borders: thinBorder(),
          margins: { top: 60, bottom: 60, left: 80, right: 120 },
          children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: value, font: 'Courier New', size: highlight ? 24 : 20, bold: highlight, color: textColor })] })],
        }),
      ],
    })
  }

  const totalsRows = [
    totalRow('Subtotal', fmtMoney(subtotal)),
    ...(discountAmount > 0 ? [totalRow('Discount', '−' + fmtMoney(discountAmount))] : []),
    ...(discountAmount > 0 ? [totalRow('After Discount', fmtMoney(discountedSubtotal))] : []),
    totalRow('GST (5%)', fmtMoney(gst)),
    totalRow('PST (7%)', fmtMoney(pst)),
    totalRow('GRAND TOTAL', fmtMoney(grandTotal), true),
  ]

  const totalsTable = new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: COL_WIDTHS,
    rows: totalsRows,
  })

  // ── DEPOSIT TABLE ───────────────────────────────────────────────────────────

  const depositTable = new Table({
    width: { size: TABLE_WIDTH, type: WidthType.DXA },
    columnWidths: [6240, 3120],
    rows: [
      new TableRow({
        children: [
          new TableCell({
            shading: { fill: LIGHT_GREY, type: ShadingType.CLEAR, color: 'auto' },
            borders: thinBorder(),
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ children: [new TextRun({ text: 'Deposit Required to Confirm Booking', font: FONT, size: 20, bold: true, color: NAVY })] })],
          }),
          new TableCell({
            shading: { fill: LIGHT_GREY, type: ShadingType.CLEAR, color: 'auto' },
            borders: thinBorder(),
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: fmtMoney(depositRequired), font: 'Courier New', size: 22, bold: true, color: NAVY })] })],
          }),
        ],
      }),
      new TableRow({
        children: [
          new TableCell({
            borders: thinBorder(),
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ children: [new TextRun({ text: 'Balance Due (after deposit)', font: FONT, size: 20, color: '5A6478' })] })],
          }),
          new TableCell({
            borders: thinBorder(),
            margins: { top: 80, bottom: 80, left: 120, right: 120 },
            children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: fmtMoney(balanceDue), font: 'Courier New', size: 20, color: '5A6478' })] })],
          }),
        ],
      }),
    ],
  })

  // ── TERMS SECTION ───────────────────────────────────────────────────────────

  const termsParagraphs = (terms || '')
    .split('\n')
    .filter(line => line.trim())
    .map(line => new Paragraph({
      spacing: { before: 60, after: 60 },
      children: [new TextRun({ text: line.trim(), font: FONT, size: 18, color: '5A6478' })],
    }))

  // ── FOOTER ──────────────────────────────────────────────────────────────────

  const footerPara = new Paragraph({
    spacing: { before: 200 },
    alignment: AlignmentType.CENTER,
    border: { top: { style: BorderStyle.SINGLE, size: 4, color: MID_GREY, space: 8 } },
    children: [
      new TextRun({ text: 'Thank you for choosing Winnipeg Tent Rental!', font: FONT, size: 18, bold: true, color: NAVY }),
      new TextRun({ text: '  ·  Questions? Contact us at your WTR email.', font: FONT, size: 18, color: '8A93A6' }),
    ],
  })

  // ── BUILD DOCUMENT ──────────────────────────────────────────────────────────

  const doc = new Document({
    styles: {
      default: {
        document: { run: { font: FONT, size: 22 } },
      },
    },
    sections: [
      {
        properties: {
          page: {
            size: { width: 12240, height: 15840 },
            margin: { top: 1080, right: 1080, bottom: 1080, left: 1080 },
          },
        },
        children: [
          headerTable,
          spacer(200),
          detailsTable,
          spacer(240),
          sectionHeading('Itemized Services'),
          spacer(80),
          lineItemsTable,
          spacer(80),
          totalsTable,
          spacer(200),
          sectionHeading('Deposit & Payment'),
          spacer(80),
          depositTable,
          ...(terms ? [
            spacer(200),
            sectionHeading('Terms & Conditions'),
            spacer(60),
            ...termsParagraphs,
          ] : []),
          footerPara,
        ],
      },
    ],
  })

  // ── TRIGGER DOWNLOAD ────────────────────────────────────────────────────────

  const blob = await Packer.toBlob(doc)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${quoteNumber || 'WTR-Quote'}.docx`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
